import { AJUDA_CATALOGO } from "@/lib/ajuda/dialogos/catalogo";
import { useEffect, useState } from "react";
import { Loader2, Search } from "lucide-react";
import { toast } from "sonner";
import type { TipoAdicionavel } from "@bookdental/schemas";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@bookdental/ui";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/cn";
import { ListaDaBase } from "./ListaDaBase";

const TITULO: Record<TipoAdicionavel, { titulo: string; plural: string; descricao: string }> = {
  servicos: { titulo: "Adicionar serviços", plural: "serviços", descricao: "Escolha da base da plataforma. Os materiais que você já tem são ligados pela química." },
  materiais: { titulo: "Adicionar materiais", plural: "materiais", descricao: "O que o dentista escolhe no pedido. Entram ligados aos seus serviços da mesma química." },
  insumos: { titulo: "Adicionar insumos", plural: "insumos", descricao: "Produtos e consumíveis da base. Sugeridos pelos seus equipamentos e materiais." },
  cores: { titulo: "Adicionar cores", plural: "cores", descricao: "Escalas de cor da base." },
  equipamentos: { titulo: "Adicionar equipamentos", plural: "equipamentos", descricao: "Sugeridos pelo perfil e pelos materiais do laboratório." },
};

const semAcento = (t: string) => t.normalize("NFD").replace(/\p{M}/gu, "").toLowerCase();

/** "Adicionar da base" do catálogo do laboratório; o que não existe na base cai no "Criar novo". */
export function AdicionarDaBaseDialog({
  tipo,
  open,
  onClose,
  onCriarNovo,
}: {
  tipo: TipoAdicionavel;
  open: boolean;
  onClose: () => void;
  onCriarNovo?: () => void;
}) {
  const utils = trpc.useUtils();
  const lista = trpc.baseDoLab.listar.useQuery({ tipo }, { enabled: open });
  const [marcados, setMarcados] = useState<Set<string>>(new Set());
  const [busca, setBusca] = useState("");
  const [soSugeridos, setSoSugeridos] = useState(true);
  useEffect(() => {
    if (!open) return;
    setMarcados(new Set());
    setBusca("");
    setSoSugeridos(true);
  }, [open, tipo]);

  const adicionar = trpc.baseDoLab.adicionar.useMutation({
    onSuccess: ({ criados, ligados }) => {
      // O catálogo inteiro se mexe junto: serviço liga material, material liga equipamento.
      utils.prosthesis.catalog.invalidate();
      utils.equipamento.invalidate();
      utils.estoque.invalidate();
      utils.baseDoLab.invalidate();
      utils.labOnboarding.progresso.invalidate();
      toast.success(`${criados + ligados} ${TITULO[tipo].plural} adicionados ao catálogo.`);
      onClose();
    },
    onError: (e) => toast.error(e.message || "Não foi possível adicionar."),
  });

  const itens = lista.data ?? [];
  const temSugestao = itens.some((i) => i.sugerido && !i.jaTem);
  const termo = semAcento(busca.trim());
  const visiveis = itens.filter(
    (i) =>
      (termo ? semAcento(`${i.nome} ${i.grupo} ${i.detalhe ?? ""}`).includes(termo) : !soSugeridos || !temSugestao || i.sugerido),
  );
  const marcar = (ids: string[], marcar: boolean) =>
    setMarcados((atual) => {
      const novo = new Set(atual);
      for (const id of ids) {
        if (marcar) novo.add(id);
        else novo.delete(id);
      }
      return novo;
    });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="flex max-h-[90vh] flex-col gap-0 border border-zinc-200 bg-white p-0 shadow-xl sm:max-w-2xl dark:border-border dark:bg-card">
        <DialogHeader className="border-b border-zinc-100 px-5 py-4 text-left dark:border-border">
          <DialogTitle ajuda={AJUDA_CATALOGO.adicionarDaBase}>{TITULO[tipo].titulo}</DialogTitle>
          <DialogDescription>{TITULO[tipo].descricao}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-2 border-b border-zinc-100 px-5 py-3 dark:border-border">
          <div className="relative min-w-[200px] flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
            <input
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder={`Buscar em ${TITULO[tipo].plural}...`}
              className="h-9 w-full rounded-md border border-zinc-200 bg-white pl-9 pr-3 text-sm focus:border-brand-500 focus:outline-none dark:border-border dark:bg-card"
            />
          </div>
          {temSugestao && !termo && (
            <div className="inline-flex rounded-md border border-zinc-200 p-0.5 dark:border-border">
              {[true, false].map((v) => (
                <button
                  key={String(v)}
                  type="button"
                  onClick={() => setSoSugeridos(v)}
                  aria-pressed={soSugeridos === v}
                  className={cn(
                    "rounded px-2.5 py-1 text-xs font-medium",
                    soSugeridos === v ? "bg-brand-50 text-brand-700 dark:bg-primary/15 dark:text-primary" : "text-zinc-600 dark:text-muted-foreground",
                  )}
                >
                  {v ? "Sugeridos" : "Todos"}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="min-h-[200px] flex-1 overflow-y-auto px-5 py-4">
          {lista.isLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-brand-600" />
            </div>
          ) : visiveis.length === 0 ? (
            <p className="py-12 text-center text-sm text-zinc-500 dark:text-muted-foreground">Nada encontrado na base.</p>
          ) : (
            <ListaDaBase itens={visiveis} marcados={marcados} onMarcar={marcar} />
          )}
        </div>

        <div className="flex flex-wrap items-center gap-3 border-t border-zinc-100 px-5 py-3 dark:border-border">
          {onCriarNovo && (
            <p className="text-xs text-zinc-500 dark:text-muted-foreground">
              Não achou?{" "}
              <button
                type="button"
                onClick={() => {
                  onClose();
                  onCriarNovo();
                }}
                className="font-semibold text-brand-700 hover:underline dark:text-primary"
              >
                Criar novo
              </button>
            </p>
          )}
          <button
            type="button"
            disabled={marcados.size === 0 || adicionar.isPending}
            onClick={() => adicionar.mutate({ tipo, ids: [...marcados] })}
            className="ml-auto inline-flex h-9 items-center gap-1.5 rounded-md bg-brand-600 px-4 text-sm font-semibold text-white shadow-sm hover:bg-brand-700 disabled:opacity-50"
          >
            {adicionar.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {marcados.size ? `Adicionar ${marcados.size}` : "Adicionar"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
